export type AdapterStatus = 'not_configured' | 'pending_credentials' | 'external_required' | 'in_review' | 'failed' | 'connected_untested' | 'connected_tested';

export interface Integration {
  id: string;
  name: string;
  status: AdapterStatus;
  detail: string;
  missing: string;
}

/** Statuses reflect observed facts only. Nothing here is "connected" until tested end-to-end. */
export const integrations: Integration[] = [
  {
    id: 'backend',
    name: 'Booking backend (server interfaces)',
    status: 'external_required',
    detail: 'Magic Patterns is frontend-only. siteConfig.apiBaseUrl is null, so the funnel runs in preview mode and sends nothing.',
    missing: 'Deployed server URL implementing /api/booking-drafts, /api/quotes, /api/deposit-checkout, webhooks, /api/crm/sync, /api/voice/booking-context.',
  },
  {
    id: 'phones',
    name: 'Phone links',
    status: 'connected_untested',
    detail: 'Kyle AI line tel:+16562680800 and human line tel:+18138510590 are linked throughout. Voice caller-ID registrations reported In Review.',
    missing: 'Live call test from the published subdomain on mobile; confirm Kyle → (813) 851-0590 transfer action and no-answer fallback.',
  },
  {
    id: 'highlevel',
    name: 'HighLevel — INSTAMOVERS FLORIDA (YT16Bu2TL9bGArn1s9P3)',
    status: 'pending_credentials',
    detail: 'No server integration. Contracts uploaded as AI knowledge are not native e-signature fields.',
    missing: 'Private integration token (server env), custom-field IDs, pipeline/stage IDs, assigned-user ID, document template IDs, published workflows.',
  },
  {
    id: 'sms',
    name: 'HighLevel SMS (A2P 10DLC)',
    status: 'failed',
    detail: 'Observed: A2P registration incomplete/failed. Business SMS is not approved.',
    missing: 'Completed A2P brand + campaign approval, published workflow, successful delivery test.',
  },
  {
    id: 'smartmoving',
    name: 'SmartMoving (transition destination)',
    status: 'pending_credentials',
    detail: 'Keep account and old jobs untouched; mirror lead data with the same booking ID. One system owns follow-up.',
    missing: 'Lead API provider key / branch ID; agreed outreach owner per lead.',
  },
  {
    id: 'places',
    name: 'Google Places + Address Validation',
    status: 'not_configured',
    detail: 'Places Autocomplete (New) adapter built (utils/places.ts): session tokens, stale-response discard, keyboard/touch listbox, Place Details address components, ZIP-conflict confirmation, per-stop search. Inactive until siteConfig.mapsBrowserKey is set — manual entry with “not verified — team reviews” meanwhile. Address Validation calls POST /api/address-validation (server).',
    missing: 'Google Cloud project with billing; Maps JavaScript API + Places API (New) enabled; browser key restricted to floridalocalmovers.instamoversfl.com; server key for Address Validation API and Routes API (road distance from 6122 W Linebaugh Ave, Tampa, FL 33625 for the ≈120-mile rule).',
  },
  {
    id: 'stripe',
    name: 'Stripe hosted Checkout ($99 deposit)',
    status: 'pending_credentials',
    detail: '“Reserve with $99” and “Prepay” buttons are locked. No card data is collected in the funnel.',
    missing: 'Authorized InstaMovers merchant, restricted secret key + webhook secret (server), refund/settlement wording.',
  },
  {
    id: 'signing',
    name: 'HighLevel Documents & Contracts',
    status: 'not_configured',
    detail: 'Document list shown per payer/method; status always “sent by our team”. No simulated signatures.',
    missing: 'Native templates with fillable card-brand checks, initials, amounts, dates, signatures; template IDs; completed-document webhook.',
  },
  {
    id: 'meta',
    name: 'Meta Pixel + CAPI (InstaMovers dataset)',
    status: 'pending_credentials',
    detail: 'Single browser Pixel 555793400074206 (utils/metaPixel.ts, no duplicate install). PageView once per load; ViewContent per selected move size (content_ids move_studio … move_5plus, property type); Lead only after the backend persists the request (eventID lead_<bookingId>); AddToCart + InitiateCheckout on the checkout button (value = $99 deposit, estimated_move_value separate). Purchase is not fired from the browser. _fbp/_fbc and fbclid go to the backend with attribution, never into URLs. No addresses, names or detailed inventory sent.',
    missing: 'Server CAPI with the same event_id for Lead/InitiateCheckout/Purchase; Purchase from the verified payment webhook only (value = amount collected); CAPI token (server); consent rule for fbp/fbc; verify the sequence in Events Manager → Test Events; domain verification for floridalocalmovers.instamoversfl.com.',
  },
  {
    id: 'dispatch',
    name: 'Dispatch availability',
    status: 'not_configured',
    detail: '8 AM / noon / 3 PM are preferences; all dates are “requested”.',
    missing: 'Real capacity source behind GET /api/availability.',
  },
  {
    id: 'media',
    name: 'Owner photo library (96 JPEGs)',
    status: 'not_configured',
    detail: 'Batches 1–5: all 96 upload URLs recorded. Load status is checked live on the Photo library tab.',
    missing: 'Verify every URL loads on the published domain.',
  },
];
